import { type MovementTuning, movementFor } from '@battle/content';
import { SIM_DT } from '@battle/shared';
import RAPIER from '@dimforge/rapier3d-deterministic-compat';
import type { ArenaCollision } from './arena-collision.ts';
import { snapDir, snapPos, snapStamina, snapVel, snapYaw } from './grid.ts';
import { Button, type Input, NEUTRAL_INPUT, yawFromInput } from './input.ts';

let ready: Promise<void> | null = null;

/** Loads Rapier's WASM once (Node and browser). Await before creating a world. */
export function initPhysics(): Promise<void> {
  ready ??= RAPIER.init();
  return ready;
}

export type Locomotion = 'ground' | 'air' | 'dodge' | 'brace';

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Plain, serializable creature state. Rapier objects are rebuilt from this, never the reverse. */
export interface CreatureState {
  handle: number;
  creatureId: string;
  pos: Vec3;
  vel: Vec3;
  /** Facing, radians. 0 faces +Z. */
  yaw: number;
  mode: Locomotion;
  grounded: boolean;
  ticksSinceGrounded: number;
  stamina: number;
  ticksSinceStaminaUse: number;
  sprinting: boolean;
  /** Set when stamina runs dry; cleared once it regenerates past `sprintRestart`. */
  sprintLocked: boolean;
  dodgeTick: number;
  dodgeDirX: number;
  dodgeDirZ: number;
  /** Remaining buffer ticks for press actions; 0 = nothing buffered. */
  bufferedJump: number;
  bufferedDodge: number;
  prevButtons: number;
  invulnerable: boolean;
}

export type SimEvent =
  | { type: 'jump'; handle: number }
  | { type: 'land'; handle: number; impact: number }
  | { type: 'dodge'; handle: number; dirX: number; dirZ: number }
  | { type: 'sprint'; handle: number; on: boolean };

interface Body {
  state: CreatureState;
  tuning: MovementTuning;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
}

export interface SimWorld {
  readonly tick: number;
  readonly creatures: readonly CreatureState[];
  addCreature(
    creatureId: string,
    spawn: { x: number; y: number; z: number; yaw: number },
    agi?: number,
  ): CreatureState;
  /** Tuning is data; the Training panel swaps it live. */
  setTuning(handle: number, tuning: MovementTuning): void;
  tuningOf(handle: number): MovementTuning;
  /** Overwrite a creature's full state (network reconciliation) and move its collider. */
  setState(handle: number, state: CreatureState): void;
  /** Place a creature (Training resets, spawns). Clears velocity. */
  teleport(handle: number, pos: Vec3, yaw?: number): void;
  /** Advance one 60 Hz tick. Inputs are keyed by creature handle; missing = neutral. */
  step(inputs: ReadonlyMap<number, Input>): SimEvent[];
  /** Order-stable hash of the simulation state, for determinism tests and desync detection. */
  hash(): number;
  /** Ray against the static arena, for camera collision and foot IK on the client. */
  castRay(origin: Vec3, dir: Vec3, maxToi: number): number | null;
  dispose(): void;
}

const DEG = Math.PI / 180;

export function createWorld(arena: ArenaCollision): SimWorld {
  if (!ready) throw new Error('call initPhysics() first');
  const world = new RAPIER.World({ x: 0, y: 0, z: 0 });
  world.timestep = SIM_DT;
  const arenaBody = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
  const arenaCollider = world.createCollider(
    // Merged vertices + internal-edge fixing, or capsules snag on the seams between faces.
    RAPIER.ColliderDesc.trimesh(
      arena.vertices,
      arena.indices,
      RAPIER.TriMeshFlags.MERGE_DUPLICATE_VERTICES | RAPIER.TriMeshFlags.FIX_INTERNAL_EDGES,
    ),
    arenaBody,
  );

  world.step(); // build the query structures so rays work before the first tick

  const controller = world.createCharacterController(0.02);
  controller.setUp({ x: 0, y: 1, z: 0 });
  controller.setMaxSlopeClimbAngle(50 * DEG);
  controller.setMinSlopeSlideAngle(55 * DEG);
  controller.enableAutostep(0.3, 0.1, false);
  controller.enableSnapToGround(0.5);
  controller.setSlideEnabled(true);

  const bodies: Body[] = [];
  let tick = 0;

  function capsuleCenterY(t: MovementTuning) {
    return t.capsule.halfHeight + t.capsule.radius;
  }

  function staminaSpend(s: CreatureState, amount: number) {
    s.stamina = Math.max(0, s.stamina - amount);
    s.ticksSinceStaminaUse = 0;
  }

  function stepCreature(b: Body, input: Input, events: SimEvent[]) {
    const s = b.state;
    const t = b.tuning;
    const dt = SIM_DT;
    const pressed = input.buttons & ~s.prevButtons;
    const held = input.buttons;
    s.prevButtons = input.buttons;

    if (pressed & Button.Jump) s.bufferedJump = t.bufferTicks;
    if (pressed & Button.Dodge) s.bufferedDodge = t.bufferTicks;

    // Camera-relative wish direction. Forward = (sin yaw, cos yaw), right = (-cos yaw, sin yaw).
    const camYaw = yawFromInput(input.yaw);
    const fx = Math.sin(camYaw);
    const fz = Math.cos(camYaw);
    const mx = input.moveX / 127;
    const my = input.moveY / 127;
    let wx = -fz * mx + fx * my;
    let wz = fx * mx + fz * my;
    const mag = Math.min(1, Math.hypot(wx, wz));
    if (mag > 1e-3) {
      const inv = 1 / Math.hypot(wx, wz);
      wx *= inv;
      wz *= inv;
    }
    const moving = mag > 0.1;

    // ---- dodge ----
    if (s.mode === 'dodge') {
      s.dodgeTick++;
      if (s.dodgeTick >= t.dodge.ticks + t.dodge.recoveryTicks)
        s.mode = s.grounded ? 'ground' : 'air';
    }
    if (s.bufferedDodge > 0 && s.mode !== 'dodge' && s.grounded && s.stamina >= t.dodge.stamina) {
      s.bufferedDodge = 0;
      staminaSpend(s, t.dodge.stamina);
      s.mode = 'dodge';
      s.dodgeTick = 0;
      if (moving) {
        s.dodgeDirX = wx;
        s.dodgeDirZ = wz;
      } else {
        s.dodgeDirX = -Math.sin(s.yaw);
        s.dodgeDirZ = -Math.cos(s.yaw);
      }
      events.push({ type: 'dodge', handle: s.handle, dirX: s.dodgeDirX, dirZ: s.dodgeDirZ });
    }
    s.invulnerable = s.mode === 'dodge' && s.dodgeTick < t.dodge.iframeTicks;

    // ---- brace / sprint ----
    if (s.mode !== 'dodge' && s.mode !== 'air') s.mode = held & Button.Brace ? 'brace' : 'ground';
    if (s.sprintLocked && s.stamina >= t.stamina.sprintRestart) s.sprintLocked = false;
    const wantSprint =
      (held & Button.Dodge) !== 0 && moving && s.mode === 'ground' && !s.sprintLocked;
    const sprinting = wantSprint && s.stamina > 0;
    if (sprinting !== s.sprinting) {
      s.sprinting = sprinting;
      events.push({ type: 'sprint', handle: s.handle, on: sprinting });
    }
    if (sprinting) {
      staminaSpend(s, t.stamina.sprintDrainPerSec * dt);
      if (s.stamina <= 0) {
        s.sprintLocked = true;
        s.sprinting = false;
        events.push({ type: 'sprint', handle: s.handle, on: false });
      }
    }

    // ---- planar velocity ----
    if (s.mode === 'dodge') {
      const recovering = s.dodgeTick >= t.dodge.ticks;
      // Ease out over the active window so the dodge reads as a burst, not a slide.
      const k = recovering ? t.dodge.recoverySpeed : 1 - 0.45 * (s.dodgeTick / t.dodge.ticks) ** 2;
      s.vel.x = s.dodgeDirX * t.dodge.speed * k;
      s.vel.z = s.dodgeDirZ * t.dodge.speed * k;
    } else {
      let speed = sprinting
        ? t.sprintSpeed
        : mag < 0.5
          ? t.walkSpeed * (mag / 0.5)
          : t.runSpeed * mag;
      if (s.mode === 'brace') speed = Math.min(speed, t.walkSpeed * t.brace.speedMul * 3);
      const tx = moving ? wx * speed : 0;
      const tz = moving ? wz * speed : 0;
      const dx = tx - s.vel.x;
      const dz = tz - s.vel.z;
      const dlen = Math.hypot(dx, dz);
      const rate = s.grounded ? (moving ? t.accel : t.decel) : t.airAccel;
      const maxDelta = rate * dt;
      if (dlen <= maxDelta) {
        s.vel.x = tx;
        s.vel.z = tz;
      } else {
        s.vel.x += (dx / dlen) * maxDelta;
        s.vel.z += (dz / dlen) * maxDelta;
      }
    }

    // ---- facing ----
    let targetYaw: number | null = null;
    if (s.mode === 'brace' || held & Button.Aim) targetYaw = camYaw;
    else if (s.mode === 'dodge') targetYaw = moving ? Math.atan2(s.dodgeDirX, s.dodgeDirZ) : null;
    else if (moving) targetYaw = Math.atan2(wx, wz);
    if (targetYaw !== null) {
      let diff = targetYaw - s.yaw;
      diff = Math.atan2(Math.sin(diff), Math.cos(diff));
      const rate = (Math.abs(diff) > 120 * DEG ? t.turnRateSharp : t.turnRate) * DEG * dt;
      s.yaw += Math.max(-rate, Math.min(rate, diff));
      s.yaw = Math.atan2(Math.sin(s.yaw), Math.cos(s.yaw));
    }

    // ---- vertical ----
    const canJump = s.mode !== 'dodge' && (s.grounded || s.ticksSinceGrounded <= t.coyoteTicks);
    if (s.bufferedJump > 0 && canJump) {
      s.bufferedJump = 0;
      s.vel.y = t.jumpVelocity;
      s.ticksSinceGrounded = t.coyoteTicks + 1;
      s.grounded = false;
      s.mode = 'air';
      events.push({ type: 'jump', handle: s.handle });
    }
    if (s.grounded && s.vel.y <= 0) {
      s.vel.y = -1; // keep contact for snap-to-ground
    } else {
      const g = t.gravity * (s.vel.y < 0 ? t.fallGravityScale : 1);
      s.vel.y = Math.max(-t.maxFallSpeed, s.vel.y - g * dt);
    }
    if (s.bufferedJump > 0) s.bufferedJump--;
    if (s.bufferedDodge > 0) s.bufferedDodge--;

    // ---- collide and move ----
    const desired = { x: s.vel.x * dt, y: s.vel.y * dt, z: s.vel.z * dt };
    controller.computeColliderMovement(b.collider, desired);
    const moved = controller.computedMovement();
    const wasGrounded = s.grounded;
    s.grounded = controller.computedGrounded();
    s.pos.x += moved.x;
    s.pos.y += moved.y;
    s.pos.z += moved.z;

    // Steep contacts (walls, pillars) remove only the velocity pushing into them, so creatures
    // slide along walls without sticking and keep full intent into steps (autostep needs it).
    // Ceilings stop the rise.
    if (s.mode !== 'dodge') {
      for (let i = 0; i < controller.numComputedCollisions(); i++) {
        const n = controller.computedCollision(i)?.normal1;
        if (!n || n.y > 0.7) continue;
        const into = s.vel.x * n.x + s.vel.z * n.z;
        if (into < 0 && moved.x * n.x + moved.z * n.z < 1e-4) {
          s.vel.x -= into * n.x;
          s.vel.z -= into * n.z;
        }
      }
    }
    if (desired.y > 0 && moved.y < desired.y * 0.5) s.vel.y = 0;

    if (s.grounded) {
      if (!wasGrounded) {
        events.push({ type: 'land', handle: s.handle, impact: -s.vel.y });
        s.vel.y = -1;
      }
      s.ticksSinceGrounded = 0;
      if (s.mode === 'air') s.mode = 'ground';
    } else {
      s.ticksSinceGrounded++;
      if (s.mode === 'ground' || s.mode === 'brace') s.mode = 'air';
    }

    // ---- stamina regen ----
    s.ticksSinceStaminaUse++;
    if (s.ticksSinceStaminaUse > t.stamina.regenDelayTicks && !sprinting) {
      s.stamina = Math.min(t.stamina.max, s.stamina + t.stamina.regenPerSec * dt);
    }
    snapToGrid(s);
    b.body.setNextKinematicTranslation({ x: s.pos.x, y: s.pos.y + capsuleCenterY(t), z: s.pos.z });
  }

  function snapToGrid(s: CreatureState) {
    s.pos.x = snapPos(s.pos.x);
    s.pos.y = snapPos(s.pos.y);
    s.pos.z = snapPos(s.pos.z);
    s.vel.x = snapVel(s.vel.x);
    s.vel.y = snapVel(s.vel.y);
    s.vel.z = snapVel(s.vel.z);
    s.yaw = snapYaw(s.yaw);
    s.stamina = snapStamina(s.stamina);
    s.dodgeDirX = snapDir(s.dodgeDirX);
    s.dodgeDirZ = snapDir(s.dodgeDirZ);
  }

  const api: SimWorld = {
    get tick() {
      return tick;
    },
    get creatures() {
      return bodies.map((b) => b.state);
    },
    addCreature(creatureId, spawn, agi = 100) {
      const tuning = movementFor(creatureId, agi);
      const body = world.createRigidBody(
        RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
          spawn.x,
          spawn.y + capsuleCenterY(tuning),
          spawn.z,
        ),
      );
      const collider = world.createCollider(
        RAPIER.ColliderDesc.capsule(tuning.capsule.halfHeight, tuning.capsule.radius),
        body,
      );
      const state: CreatureState = {
        handle: bodies.length,
        creatureId,
        pos: { x: spawn.x, y: spawn.y, z: spawn.z },
        vel: { x: 0, y: 0, z: 0 },
        yaw: spawn.yaw,
        mode: 'ground',
        grounded: false,
        ticksSinceGrounded: 0,
        stamina: tuning.stamina.max,
        ticksSinceStaminaUse: 999,
        sprinting: false,
        sprintLocked: false,
        dodgeTick: 0,
        dodgeDirX: 0,
        dodgeDirZ: 0,
        bufferedJump: 0,
        bufferedDodge: 0,
        prevButtons: 0,
        invulnerable: false,
      };
      snapToGrid(state);
      bodies.push({ state, tuning, body, collider });
      world.step();
      return state;
    },
    setTuning(handle, tuning) {
      const b = bodies[handle];
      if (!b) return;
      const capsuleChanged =
        tuning.capsule.radius !== b.tuning.capsule.radius ||
        tuning.capsule.halfHeight !== b.tuning.capsule.halfHeight;
      b.tuning = tuning;
      if (capsuleChanged) {
        world.removeCollider(b.collider, false);
        b.collider = world.createCollider(
          RAPIER.ColliderDesc.capsule(tuning.capsule.halfHeight, tuning.capsule.radius),
          b.body,
        );
      }
    },
    setState(handle, next) {
      const b = bodies[handle];
      if (!b) return;
      const { handle: _h, creatureId: _c, ...rest } = next;
      Object.assign(b.state, rest, { pos: { ...rest.pos }, vel: { ...rest.vel } });
      const p = b.state.pos;
      b.body.setTranslation({ x: p.x, y: p.y + capsuleCenterY(b.tuning), z: p.z }, true);
      // Colliders only follow their body on the next step; the controller queries the collider,
      // so sync it now or the first replayed tick collides from the old position.
      world.propagateModifiedBodyPositionsToColliders();
    },
    teleport(handle, pos, yaw) {
      const b = bodies[handle];
      if (!b) return;
      Object.assign(b.state.pos, pos);
      b.state.vel = { x: 0, y: 0, z: 0 };
      if (yaw !== undefined) b.state.yaw = yaw;
      b.body.setTranslation({ x: pos.x, y: pos.y + capsuleCenterY(b.tuning), z: pos.z }, true);
      world.step();
    },
    tuningOf(handle) {
      return (bodies[handle] as Body).tuning;
    },
    step(inputs) {
      const events: SimEvent[] = [];
      for (const b of bodies) stepCreature(b, inputs.get(b.state.handle) ?? NEUTRAL_INPUT, events);
      world.step();
      tick++;
      return events;
    },
    hash() {
      // FNV-1a over float32 bit patterns, in handle order.
      const f = new Float32Array(1);
      const u = new Uint32Array(f.buffer);
      let h = 0x811c9dc5;
      const mix = (v: number) => {
        f[0] = v;
        h = Math.imul(h ^ (u[0] as number), 0x01000193) >>> 0;
      };
      mix(tick);
      for (const { state: s } of bodies) {
        for (const v of [
          s.pos.x,
          s.pos.y,
          s.pos.z,
          s.vel.x,
          s.vel.y,
          s.vel.z,
          s.yaw,
          s.stamina,
          s.dodgeTick,
        ])
          mix(v);
        mix(['ground', 'air', 'dodge', 'brace'].indexOf(s.mode));
      }
      return h;
    },
    castRay(origin, dir, maxToi) {
      const hit = world.castRay(
        new RAPIER.Ray(origin, dir),
        maxToi,
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        (c) => c.handle === arenaCollider.handle,
      );
      return hit ? hit.timeOfImpact : null;
    },
    dispose() {
      world.free();
    },
  };
  return api;
}
